import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { ShowHeader } from '@/common/components/ShowHeader'
import useDebounce from '@/common/hooks/useDebounce'
import { cn } from '@/common/utils/cn'
import { formatCurrency, formatDate } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import useBillingBill from '@/features/billingBills/hooks/useBillingBill'
import { CreatePaymentAdvanceModal } from '@/features/paymentAdvances/components/CreatePaymentAdvanceModal'
import { PaymentAdvanceConfirmModal } from '@/features/paymentAdvances/components/PaymentAdvanceConfirmModal'
import { ResumeItem } from '@/features/paymentAdvances/components/ResumeItem'
import { useCreatePaymentAdvance } from '@/features/paymentAdvances/hooks/useCreatePaymentAdvance'
import { usePaymentAdvanceEstimate } from '@/features/paymentAdvances/hooks/usePaymentAdvanceEstimate'
import {
  createPaymentAdvanceSchemaFactory,
  CreatePaymentAdvanceValidationSchema,
} from '@/features/paymentAdvances/schemas/create.schema'
import { PaymentAdvanceEstimateData } from '@/features/paymentAdvances/types/paymentAdvanceEstimate.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import PaymentAdvanceCreateNotAllowed from '../CreateNotAllowed'
import PaymentAdvanceCreateLoadingState from '../../components/CreatePaymentAdvanceLoadingState'

const PaymentAdvanceCreate = () => {
  const { billId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [showNotAllowedScreen, setShowNotAllowedScreen] = useState<boolean>()

  const [requestedTotal, setRequestedTotal] = useDebounce('')
  const [dateToBePaid, setDateToBePaid] = useDebounce('')
  const [estimateData, setEstimateData] = useState<PaymentAdvanceEstimateData>()
  const [modalCreatePaymentAdvanceIsOpen, setModalCreatePaymentAdvanceIsOpen] = useState(false)
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { data: bill, isPending: billIsPending } = useBillingBill({ id: billId })

  const methods = useForm<CreatePaymentAdvanceValidationSchema>({
    resolver: zodResolver(createPaymentAdvanceSchemaFactory(bill?.due_at)),
    shouldFocusError: false,
    defaultValues: {
      requested_total: '',
      date_to_be_paid: '',
    },
  })

  const {
    control,
    setValue,
    setError,
    handleSubmit,
    getValues,
    formState: { errors },
  } = methods

  const { date_to_be_paid } = getValues()

  const { mutate: estimatePaymentAdvance, isPending: estimatePaymentAdvanceIsPending } = usePaymentAdvanceEstimate({
    onSuccess: (response) => {
      setEstimateData(response.data.data)

      if (showNotAllowedScreen === undefined)
        setShowNotAllowedScreen(!response.data.data.can_advance)
    },
    onError: FormTransformers.errorsTransformer<CreatePaymentAdvanceValidationSchema>({ setError, setMessages }),
  })

  const { mutate: createPaymentAdvance, isPending: createPaymentAdvanceIsPending } = useCreatePaymentAdvance({
    onSuccess: () => {
      setModalCreatePaymentAdvanceIsOpen(false)
      setConfirmModalIsOpen(true)
    },
    onError: FormTransformers.errorsTransformer<CreatePaymentAdvanceValidationSchema>({ setError, setMessages }),
  })

  const submitEstimate: SubmitHandler<CreatePaymentAdvanceValidationSchema> = (data) => {
    if (billId) {
      setEstimateData(undefined)
      estimatePaymentAdvance({ ...data, bill_id: billId })
    }
  }

  const submitOnChange = handleSubmit(submitEstimate)

  const onSubmit: SubmitHandler<CreatePaymentAdvanceValidationSchema> = () => setModalCreatePaymentAdvanceIsOpen(true)

  const confirmCreatePaymentAdvance = () => {
    if (billId && estimateData) {
      createPaymentAdvance({
        bill_id: billId,
        requested_total: estimateData.total_that_will_be_paid,
        date_to_be_paid,
      })
    }
  }

  const onConfirm = () => {
    setConfirmModalIsOpen(false)
    queryClient.invalidateQueries({ queryKey: ['paymentAdvances'] })
    navigate('/payment-advances')
  }

  useEffect(() => {
    if (!requestedTotal && !dateToBePaid) {
      return setEstimateData(undefined)
    }

    submitOnChange()
  }, [requestedTotal, dateToBePaid])

  useEffect(() => {
    if (billIsPending) {
      return
    }

    if (bill?.total_amount) {
      setRequestedTotal(formatCurrency(Number(bill.total_amount)))
      setValue('requested_total', formatCurrency(Number(bill.total_amount)))
    }

    const tomorrow = new Date()

    if (bill?.closed_at && new Date(bill?.closed_at).toDateString() === new Date().toDateString()) {
      tomorrow.setDate(tomorrow.getDate() + 2)
      const tomorrowFormatted = formatDate(tomorrow)

      setValue('date_to_be_paid', tomorrowFormatted)
      setDateToBePaid(tomorrowFormatted)
      return
    }

    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowFormatted = formatDate(tomorrow)

    setValue('date_to_be_paid', tomorrowFormatted)
    setDateToBePaid(tomorrowFormatted)
  }, [billIsPending])

  if (estimateData && showNotAllowedScreen) {
    return <PaymentAdvanceCreateNotAllowed estimateData={estimateData} />
  }

  if (!estimateData) {
    return <PaymentAdvanceCreateLoadingState />
  }

  return (
    <div>
      <ShowHeader goBack backTo="/payment-advances" title="Antecipar recebíveis" />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className="max-h-[calc((100vh-104px-1.5rem))] overflow-y-auto space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <InfoIcon strokeWidth={1} className="size-6" color="var(--warning)" />

                <div className="flex flex-col">
                  <p className="text-[10px] font-bold leading-3">
                    Ao antecipar o valor recebível de uma fatura você estará sujeito ao pagamento de Juros.
                  </p>

                  <p className="text-[10px] font-light italic leading-3">
                    Atente-se ao juros referente a data selecionada para pagamento.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <p>Detalhes da fatura</p>

                <div className="flex gap-4">
                  <div>
                    <p className="text-sm font-light italic">Fatura</p>

                    <p className="text-lg font-bold">
                      Fatura {bill?.opened_at ? formatDate(new Date(bill?.opened_at)) : ''} -{' '}
                      {bill?.closed_at ? formatDate(new Date(bill?.closed_at)) : ''}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-light italic">Saldo da fatura</p>

                    <p className="text-lg font-bold">{formatCurrency(Number(bill?.total_amount || 0))}</p>
                  </div>

                  <div>
                    <p className="text-sm font-light italic">Data para recebimento</p>

                    <p className="text-lg font-bold">{bill?.due_at ? formatDate(new Date(bill.due_at)) : '-'}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Controller
                  control={control}
                  name="requested_total"
                  disabled={billIsPending || createPaymentAdvanceIsPending}
                  render={({ field: { onChange, ...rest } }) => (
                    <CurrencyTextField
                      label="Valor desejado para antecipação"
                      markAsRequired
                      className="w-full"
                      maxValue={Number(bill?.total_amount || 0)}
                      {...rest}
                      onChange={(e) => {
                        setRequestedTotal((e.target as HTMLInputElement).value)
                        onChange(e)
                      }}
                      errorMessage={errors?.requested_total?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="date_to_be_paid"
                  disabled={billIsPending || createPaymentAdvanceIsPending}
                  render={({ field: { onChange, ...rest } }) => (
                    <MaskedTextField
                      label="Data para recebimento"
                      markAsRequired
                      mask="00/00/0000"
                      placeholder="DD/MM/AAAA"
                      className="w-full"
                      {...rest}
                      onChange={(e) => {
                        setDateToBePaid((e.target as HTMLInputElement).value)
                        onChange(e)
                      }}
                      errorMessage={errors?.date_to_be_paid?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="space-y-4">
              <p>Cálculo de juros</p>

              <div className="flex items-center gap-2">
                <InfoIcon strokeWidth={1} className="size-6" color="var(--warning)" />

                <div className="flex flex-col">
                  <p className="text-[10px] font-bold leading-3">
                    Valor máximo antecipável e o valor máximo a pagar de juros são proporcionais a data para
                    recebimento.
                  </p>
                  <p className="text-[10px] font-light italic leading-3">
                    Atente-se a data selecionada e o juros cobrado.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[1fr_1fr_180px] gap-4">
                <ResumeItem
                  label="Valor máximo antecipável"
                  value={formatCurrency(Number(estimateData?.max_interested_available_to_be_advanced ?? 0))}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                />

                <ResumeItem
                  label="Valor máximo a pagar de juros antecipável"
                  value={formatCurrency(Number(estimateData?.max_interest ?? 0))}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                />

                <ResumeItem
                  label="Juros ao dia"
                  value={`${Number(estimateData?.daily_interest ?? 0) * 100}%`}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                  className="row-span-2"
                />

                <ResumeItem
                  label="Valor mínimo antecipável"
                  value={formatCurrency(Number(estimateData?.min_interested_available_to_be_advanced ?? 0))}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                />

                <ResumeItem
                  label="Valor mínimo a pagar de juros antecipável"
                  value={formatCurrency(Number(estimateData?.min_interest ?? 0))}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                />
              </div>
            </div>

            <div className="w-full h-px bg-white-300" />

            <div className="grid grid-cols-2 gap-4 items-start">
              <div className="space-y-1">
                <ResumeItem
                  label="Valor a receber antecipado"
                  value={formatCurrency(Number(estimateData?.total_that_will_be_paid ?? 0))}
                  valueIsPending={estimatePaymentAdvanceIsPending}
                  className={cn('bg-success-disabled', estimateData?.discounted_in_advance && 'bg-[#FCCDCA33]')}
                />

                {estimateData?.discounted_in_advance && (
                  <div className="text-[10px] text-danger-soft leading-3">
                    <p className="font-bold">Valor solicitado acima do valor máximo.</p>
                    <p className="font-light italic">O valor foi alterado para o valor máximo antecipável.</p>
                  </div>
                )}
              </div>

              <ResumeItem
                label="Valor a pagar de juros"
                value={`${formatCurrency(Number(estimateData?.interest_total ?? 0))} (${Number(estimateData?.final_interest_percentage ?? 0) * 100
                  }%)`}
                valueIsPending={estimatePaymentAdvanceIsPending}
                className="bg-yellow-op-2"
              />
            </div>

            <div className="flex gap-4 items-end">
              <div className="grid w-full grid-cols-2 gap-4">
                <ResumeItem
                  label="Valor a ser abatido da fatura"
                  value={formatCurrency(Number(estimateData?.discounted_from_bill ?? 0))}
                />

                <ResumeItem
                  label="Saldo da fatura após antecipação"
                  value={
                    estimateData
                      ? formatCurrency(Number(estimateData.bill_balance_after_advance))
                      : formatCurrency(Number(bill?.total_amount || 0))
                  }
                  valueIsPending={estimatePaymentAdvanceIsPending}
                />
              </div>

              <Button
                variant="default"
                type="submit"
                className="text-nowrap px-6"
                isLoading={createPaymentAdvanceIsPending}
                disabled={
                  billIsPending || estimatePaymentAdvanceIsPending || !estimateData || createPaymentAdvanceIsPending
                }
              >
                Solicitar antecipação
              </Button>
            </div>
          </Box>
        </form>
      </FormProvider>

      <CreatePaymentAdvanceModal
        isOpen={modalCreatePaymentAdvanceIsOpen}
        setIsOpen={setModalCreatePaymentAdvanceIsOpen}
        estimateData={estimateData}
        dateToBePaid={date_to_be_paid}
        confirmCreatePaymentAdvance={confirmCreatePaymentAdvance}
        createPaymentAdvanceIsPending={createPaymentAdvanceIsPending}
      />

      <PaymentAdvanceConfirmModal
        isOpen={confirmModalIsOpen}
        setIsOpen={setConfirmModalIsOpen}
        message="Adiantamento solicitado!"
        showResume
        totalToBePaid={estimateData?.total_that_will_be_paid}
        dateToBePaid={date_to_be_paid}
        onConfirm={onConfirm}
      />

      {messages && <ModalMessageSimple messages={messages as string[]} setMessages={setMessages} isError />}
    </div>
  )
}

export default PaymentAdvanceCreate
